'use client';

import * as React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import {
  Anchor,
  Ship,
  FileText,
  Search,
  MessageCircle,
  Zap,
  Users,
  BarChart3,
  Globe,
  Shield,
  ChevronRight,
  ChevronDown,
  Sparkles,
  ArrowRight,
  Building2,
  Key,
  Receipt,
  AlertTriangle,
  Calendar,
  Car,
  Settings,
  UserCog,
  Gauge,
  Bot,
  Clock,
  FolderOpen,
  MapPin,
  Calculator,
  TrendingUp,
  Bell,
  FileCheck,
  Waves,
  Crown,
  Wrench,
  ClipboardList,
  QrCode,
  Eye,
  BrainCircuit,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Feature category data structure
interface Feature {
  icon: React.ElementType;
  titleDe: string;
  titleEn: string;
  descDe: string;
  descEn: string;
  roles?: ('owner' | 'operator' | 'staff')[];
  highlight?: boolean;
}

interface FeatureCategory {
  id: string;
  iconComponent: React.ElementType;
  titleDe: string;
  titleEn: string;
  descDe: string;
  descEn: string;
  gradient: string;
  features: Feature[];
}

const featureCategories: FeatureCategory[] = [
  {
    id: 'core',
    iconComponent: Building2,
    titleDe: 'Marina-Verwaltung',
    titleEn: 'Marina Management',
    descDe: 'Zentrale Verwaltung aller Marina-Operationen',
    descEn: 'Central management of all marina operations',
    gradient: 'from-blue-600 to-cyan-500',
    features: [
      {
        icon: Building2,
        titleDe: 'Multi-Marina Support',
        titleEn: 'Multi-Marina Support',
        descDe: 'Verwalten Sie mehrere Marinas von einem Dashboard aus',
        descEn: 'Manage multiple marinas from a single dashboard',
        roles: ['owner'],
        highlight: true,
      },
      {
        icon: Anchor,
        titleDe: 'Liegeplatz-Management',
        titleEn: 'Berth Management',
        descDe: 'Vollständige Übersicht aller Liegeplätze, Sektionen und Belegung',
        descEn: 'Complete overview of all berths, sections, and occupancy',
        roles: ['owner', 'operator'],
      },
      {
        icon: Users,
        titleDe: 'Mieter-Verwaltung',
        titleEn: 'Tenant Management',
        descDe: 'Umfassende Mieterprofile mit Kontaktdaten, Booten und Historie',
        descEn: 'Comprehensive tenant profiles with contacts, boats, and history',
        roles: ['owner', 'operator', 'staff'],
      },
      {
        icon: FileText,
        titleDe: 'Vertrags-Management',
        titleEn: 'Contract Management',
        descDe: 'Digitale Verträge mit automatischer Verlängerungsverfolgung',
        descEn: 'Digital contracts with automatic renewal tracking',
        roles: ['owner', 'operator'],
      },
      {
        icon: MapPin,
        titleDe: 'Interaktive Hafenübersicht',
        titleEn: 'Interactive Marina Map',
        descDe: 'Visuelle Darstellung aller Liegeplätze mit Belegungsstatus',
        descEn: 'Visual representation of all berths with occupancy status',
        roles: ['owner', 'operator', 'staff'],
      },
    ],
  },
  {
    id: 'financial',
    iconComponent: Receipt,
    titleDe: 'Finanzverwaltung',
    titleEn: 'Financial Management',
    descDe: 'Rechnungsstellung, Mahnwesen und Finanzberichte',
    descEn: 'Invoicing, debt collection, and financial reports',
    gradient: 'from-emerald-600 to-teal-500',
    features: [
      {
        icon: Receipt,
        titleDe: 'Rechnungsstellung',
        titleEn: 'Invoicing',
        descDe: 'Erstellen und verwalten Sie Rechnungen mit automatischer PDF-Generierung',
        descEn: 'Create and manage invoices with automatic PDF generation',
        roles: ['owner', 'operator'],
      },
      {
        icon: Bell,
        titleDe: 'Mahnwesen',
        titleEn: 'Dunning Management',
        descDe: 'Automatische Zahlungserinnerungen per WhatsApp und E-Mail',
        descEn: 'Automatic payment reminders via WhatsApp and email',
        roles: ['owner', 'operator'],
        highlight: true,
      },
      {
        icon: Zap,
        titleDe: 'Stromabrechnung',
        titleEn: 'Electricity Billing',
        descDe: 'Smart-Meter-Integration mit automatischer Verbrauchsabrechnung',
        descEn: 'Smart meter integration with automatic consumption billing',
        roles: ['owner', 'operator'],
      },
      {
        icon: TrendingUp,
        titleDe: 'Finanzberichte',
        titleEn: 'Financial Reports',
        descDe: 'Umsatz-, Auslastungs- und Trendanalysen',
        descEn: 'Revenue, occupancy, and trend analysis',
        roles: ['owner'],
      },
      {
        icon: Calculator,
        titleDe: 'Kontenabstimmung',
        titleEn: 'Account Reconciliation',
        descDe: 'Automatische Zuordnung von Zahlungen zu Rechnungen',
        descEn: 'Automatic matching of payments to invoices',
        roles: ['owner', 'operator'],
      },
      {
        icon: QrCode,
        titleDe: 'GiroCode-Zahlungen',
        titleEn: 'GiroCode Payments',
        descDe: 'QR-Code auf Rechnungen für schnelle Banküberweisung mit jeder Banking-App',
        descEn: 'QR code on invoices for quick bank transfer with any banking app',
        roles: ['owner', 'operator'],
        highlight: true,
      },
    ],
  },
  {
    id: 'communication',
    iconComponent: MessageCircle,
    titleDe: 'Kommunikation',
    titleEn: 'Communication',
    descDe: 'Direkte Kommunikation mit Mietern',
    descEn: 'Direct communication with tenants',
    gradient: 'from-green-600 to-emerald-500',
    features: [
      {
        icon: MessageCircle,
        titleDe: 'WhatsApp Integration',
        titleEn: 'WhatsApp Integration',
        descDe: 'Persönliche Kommunikation mit Mietern und Gästen direkt über WhatsApp',
        descEn: 'Personal communication with tenants and guests directly via WhatsApp',
        roles: ['owner', 'operator', 'staff'],
        highlight: true,
      },
      {
        icon: Search,
        titleDe: 'Volltextsuche',
        titleEn: 'Full-Text Search',
        descDe: 'Finden Sie alles in Sekunden - Mieter, Verträge, Rechnungen, Boote',
        descEn: 'Find anything in seconds - tenants, contracts, invoices, boats',
        roles: ['owner', 'operator', 'staff'],
        highlight: true,
      },
      {
        icon: FolderOpen,
        titleDe: 'Dokumentenverwaltung',
        titleEn: 'Document Management',
        descDe: 'Zentrale Ablage für alle Verträge, Dokumente und Korrespondenz',
        descEn: 'Central storage for all contracts, documents, and correspondence',
        roles: ['owner', 'operator'],
      },
      {
        icon: BrainCircuit,
        titleDe: 'WhatsApp KI-Assistent',
        titleEn: 'WhatsApp AI Assistant',
        descDe: 'KI-gestützte Antworten auf Mieteranfragen per WhatsApp - 24/7 verfügbar',
        descEn: 'AI-powered responses to tenant inquiries via WhatsApp - available 24/7',
        roles: ['owner', 'operator'],
        highlight: true,
      },
    ],
  },
  {
    id: 'ai',
    iconComponent: Bot,
    titleDe: 'KI & Automatisierung',
    titleEn: 'AI & Automation',
    descDe: 'Intelligente Funktionen zur Zeitersparnis',
    descEn: 'Intelligent features to save time',
    gradient: 'from-violet-600 to-purple-500',
    features: [
      {
        icon: Bot,
        titleDe: 'KI-Vertragsverarbeitung',
        titleEn: 'AI Contract Processing',
        descDe: 'Laden Sie PDF-Verträge hoch - KI extrahiert automatisch alle relevanten Daten',
        descEn: 'Upload PDF contracts - AI automatically extracts all relevant data',
        roles: ['owner', 'operator'],
        highlight: true,
      },
      {
        icon: Clock,
        titleDe: 'Automatische Erinnerungen',
        titleEn: 'Automatic Reminders',
        descDe: 'Automatische Benachrichtigungen für Zahlungen und Vertragsverlängerungen',
        descEn: 'Automatic notifications for payments and contract renewals',
        roles: ['owner', 'operator'],
      },
      {
        icon: FileCheck,
        titleDe: 'Intelligente Datenextraktion',
        titleEn: 'Smart Data Extraction',
        descDe: 'Automatische Erkennung von Mieterdaten, Preisen und Laufzeiten',
        descEn: 'Automatic recognition of tenant data, prices, and terms',
        roles: ['owner', 'operator'],
      },
    ],
  },
  {
    id: 'operations',
    iconComponent: Wrench,
    titleDe: 'Betriebsführung',
    titleEn: 'Operations',
    descDe: 'Tägliche Betriebsabläufe effizient verwalten',
    descEn: 'Efficiently manage daily operations',
    gradient: 'from-orange-600 to-amber-500',
    features: [
      {
        icon: Key,
        titleDe: 'Schlüsselverwaltung',
        titleEn: 'Key Management',
        descDe: 'Verfolgen Sie alle Schlüssel, Zuweisungen und Rückgaben',
        descEn: 'Track all keys, assignments, and returns',
        roles: ['operator', 'staff'],
      },
      {
        icon: AlertTriangle,
        titleDe: 'Vorfallmanagement',
        titleEn: 'Incident Management',
        descDe: 'Erfassen und verfolgen Sie Vorfälle und Beschädigungen',
        descEn: 'Record and track incidents and damages',
        roles: ['operator', 'staff'],
      },
      {
        icon: Car,
        titleDe: 'Parkplatzverwaltung',
        titleEn: 'Parking Management',
        descDe: 'Verwalten Sie Parkplätze und Zuweisungen',
        descEn: 'Manage parking spaces and assignments',
        roles: ['operator', 'staff'],
      },
      {
        icon: Calendar,
        titleDe: 'Veranstaltungen',
        titleEn: 'Events',
        descDe: 'Planen und kommunizieren Sie Marina-Veranstaltungen',
        descEn: 'Plan and communicate marina events',
        roles: ['owner', 'operator'],
      },
      {
        icon: Ship,
        titleDe: 'Bootsbewegungen',
        titleEn: 'Boat Movements',
        descDe: 'Protokollieren Sie Ein- und Ausfahrten',
        descEn: 'Log arrivals and departures',
        roles: ['operator', 'staff'],
      },
    ],
  },
  {
    id: 'guest',
    iconComponent: Waves,
    titleDe: 'Gastservice',
    titleEn: 'Guest Services',
    descDe: 'Gastliegeplätze und Besucherverwaltung',
    descEn: 'Guest berths and visitor management',
    gradient: 'from-cyan-600 to-blue-500',
    features: [
      {
        icon: Waves,
        titleDe: 'Online-Reservierung',
        titleEn: 'Online Booking',
        descDe: 'Gäste können Liegeplätze online reservieren - mit sofortiger Bestätigung',
        descEn: 'Guests can book berths online - with instant confirmation',
        roles: ['owner', 'operator'],
        highlight: true,
      },
      {
        icon: Users,
        titleDe: 'Besucherverwaltung',
        titleEn: 'Visitor Management',
        descDe: 'Verfolgen Sie Besucher und temporäre Zugänge',
        descEn: 'Track visitors and temporary access',
        roles: ['operator', 'staff'],
      },
      {
        icon: ClipboardList,
        titleDe: 'Check-in/Check-out',
        titleEn: 'Check-in/Check-out',
        descDe: 'Einfacher Prozess für Gastankünfte und -abfahrten',
        descEn: 'Simple process for guest arrivals and departures',
        roles: ['operator', 'staff'],
      },
    ],
  },
  {
    id: 'admin',
    iconComponent: Settings,
    titleDe: 'Administration',
    titleEn: 'Administration',
    descDe: 'Benutzerverwaltung und Systemeinstellungen',
    descEn: 'User management and system settings',
    gradient: 'from-slate-600 to-gray-500',
    features: [
      {
        icon: UserCog,
        titleDe: 'Benutzerverwaltung',
        titleEn: 'User Management',
        descDe: 'Erstellen Sie Mitarbeiterkonten mit individuellen Zugriffsrechten',
        descEn: 'Create staff accounts with individual access rights',
        roles: ['owner'],
      },
      {
        icon: Shield,
        titleDe: 'Rollenverwaltung',
        titleEn: 'Role Management',
        descDe: 'Definieren Sie benutzerdefinierte Rollen und Berechtigungen',
        descEn: 'Define custom roles and permissions',
        roles: ['owner'],
      },
      {
        icon: Globe,
        titleDe: 'Mehrsprachigkeit',
        titleEn: 'Multi-Language',
        descDe: 'Vollständige Unterstützung für Deutsch und Englisch',
        descEn: 'Full support for German and English',
        roles: ['owner', 'operator', 'staff'],
      },
      {
        icon: Settings,
        titleDe: 'Systemeinstellungen',
        titleEn: 'System Settings',
        descDe: 'Konfigurieren Sie Preise, Texte und Automatisierungen',
        descEn: 'Configure prices, texts, and automations',
        roles: ['owner'],
      },
      {
        icon: Eye,
        titleDe: 'Mieter-Ansicht',
        titleEn: 'Tenant View',
        descDe: 'Als Admin das Mieterportal aus Sicht eines Mieters sehen - ohne Passwort',
        descEn: 'As admin, view the tenant portal from a tenant\'s perspective - without password',
        roles: ['owner'],
        highlight: true,
      },
    ],
  },
  {
    id: 'reports',
    iconComponent: BarChart3,
    titleDe: 'Berichte & Analysen',
    titleEn: 'Reports & Analytics',
    descDe: 'Umfassende Einblicke in Ihre Marina-Performance',
    descEn: 'Comprehensive insights into your marina performance',
    gradient: 'from-indigo-600 to-blue-500',
    features: [
      {
        icon: Gauge,
        titleDe: 'Dashboard-Analysen',
        titleEn: 'Dashboard Analytics',
        descDe: 'Echtzeit-Übersicht über Auslastung, Umsatz und Aktivitäten',
        descEn: 'Real-time overview of occupancy, revenue, and activities',
        roles: ['owner', 'operator'],
      },
      {
        icon: BarChart3,
        titleDe: 'Cross-Marina Berichte',
        titleEn: 'Cross-Marina Reports',
        descDe: 'Vergleichen Sie Performance über mehrere Marinas hinweg',
        descEn: 'Compare performance across multiple marinas',
        roles: ['owner'],
      },
      {
        icon: Zap,
        titleDe: 'Stromverbrauchsanalyse',
        titleEn: 'Electricity Analysis',
        descDe: 'Detaillierte Verbrauchsstatistiken pro Liegeplatz',
        descEn: 'Detailed consumption statistics per berth',
        roles: ['owner', 'operator'],
      },
    ],
  },
];

// Role badge colors
const roleColors = {
  owner: 'bg-amber-100 text-amber-800 border-amber-200',
  operator: 'bg-blue-100 text-blue-800 border-blue-200',
  staff: 'bg-green-100 text-green-800 border-green-200',
};

const roleLabels = {
  owner: { de: 'Eigentümer', en: 'Owner' },
  operator: { de: 'Betreiber', en: 'Operator' },
  staff: { de: 'Mitarbeiter', en: 'Staff' },
};

// Expandable feature category component
function FeatureCategorySection({
  category,
  isOpen,
  onToggle,
  locale,
}: {
  category: FeatureCategory;
  isOpen: boolean;
  onToggle: () => void;
  locale: string;
}) {
  const Icon = category.iconComponent;
  const isDE = locale === 'de';

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl">
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.gradient} flex items-center justify-center shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {isDE ? category.titleDe : category.titleEn}
            </h3>
            <p className="text-sm text-gray-500">
              {isDE ? category.descDe : category.descEn}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400 hidden sm:block">
            {category.features.length} {isDE ? 'Funktionen' : 'Features'}
          </span>
          <ChevronDown
            className={cn(
              'w-5 h-5 text-gray-400 transition-transform duration-300',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Expandable content */}
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-6 pb-6 grid gap-4">
          {category.features.map((feature, idx) => {
            const FeatureIcon = feature.icon;
            return (
              <div
                key={idx}
                className={cn(
                  'flex items-start gap-4 p-4 rounded-xl transition-colors',
                  feature.highlight
                    ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100'
                    : 'bg-gray-50 hover:bg-gray-100'
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                  <FeatureIcon className="w-5 h-5 text-gray-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-900">
                      {isDE ? feature.titleDe : feature.titleEn}
                    </h4>
                    {feature.highlight && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full">
                        {isDE ? 'Highlight' : 'Featured'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {isDE ? feature.descDe : feature.descEn}
                  </p>
                  {feature.roles && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {feature.roles.map((role) => (
                        <span
                          key={role}
                          className={cn(
                            'px-2 py-0.5 text-xs font-medium rounded-full border',
                            roleColors[role]
                          )}
                        >
                          {isDE ? roleLabels[role].de : roleLabels[role].en}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Language switcher component
function LanguageSwitcher({ locale, onChange }: { locale: string; onChange: (locale: string) => void }) {
  return (
    <div className="flex items-center gap-1 bg-white/10 backdrop-blur-sm rounded-full p-1 border border-white/20">
      <button
        onClick={() => onChange('de')}
        className={cn(
          'px-3 py-1.5 text-sm font-medium rounded-full transition-all',
          locale === 'de'
            ? 'bg-white text-blue-900'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        )}
      >
        DE
      </button>
      <button
        onClick={() => onChange('en')}
        className={cn(
          'px-3 py-1.5 text-sm font-medium rounded-full transition-all',
          locale === 'en'
            ? 'bg-white text-blue-900'
            : 'text-white/80 hover:text-white hover:bg-white/10'
        )}
      >
        EN
      </button>
    </div>
  );
}

export default function MarketingPage() {
  const [locale, setLocale] = React.useState<string>('de');
  const [openCategories, setOpenCategories] = React.useState<Set<string>>(new Set(['core', 'ai']));

  // Get initial locale from cookie
  const validLocales = ['de', 'en', 'es', 'zh', 'at', 'no', 'nl', 'gsw'];
  React.useEffect(() => {
    const cookieLocale = document.cookie.split('; ').find(row => row.startsWith('NEXT_LOCALE='))?.split('=')[1];
    if (cookieLocale && validLocales.includes(cookieLocale)) {
      setLocale(cookieLocale);
    }
  }, []);

  const handleLocaleChange = (newLocale: string) => {
    setLocale(newLocale);
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000`;
    // Reload to apply locale change across the app
    window.location.reload();
  };

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setOpenCategories(new Set(featureCategories.map((c) => c.id)));
  };

  const collapseAll = () => {
    setOpenCategories(new Set());
  };

  const isDE = locale === 'de';

  const t = {
    hero: {
      badge: isDE ? 'KI-gestützte Marina-Verwaltung' : 'AI-Powered Marina Management',
      title1: isDE ? 'Intelligente' : 'Smart',
      title2: isDE ? 'Marina-Verwaltung' : 'Marina Management',
      subtitle: isDE
        ? 'Die moderne Plattform für effiziente Marina-Operationen. KI-gestützte Vertragsverarbeitung, sofortige Suche und direkte Mieterkommunikation.'
        : 'The modern platform for efficient marina operations. AI-powered contract processing, instant search, and direct tenant communication.',
      cta1: isDE ? 'Demo anfragen' : 'Request Demo',
      cta2: isDE ? 'Gastliegeplatz buchen' : 'Book Guest Berth',
    },
    features: {
      badge: isDE ? 'Umfassende Funktionen' : 'Comprehensive Features',
      title1: isDE ? 'Alles was Sie brauchen für' : 'Everything You Need to',
      title2: isDE ? 'Ihre Marina' : 'Manage Your Marina',
      subtitle: isDE
        ? 'Von der Vertragsverwaltung bis zur Mieterkommunikation - Marina Master optimiert jeden Aspekt Ihrer Marina-Operationen.'
        : 'From contract management to tenant communication - Marina Master streamlines every aspect of marina operations.',
      expandAll: isDE ? 'Alle öffnen' : 'Expand All',
      collapseAll: isDE ? 'Alle schließen' : 'Collapse All',
    },
    roles: {
      title: isDE ? 'Maßgeschneidert für jede Rolle' : 'Tailored for Every Role',
      owner: {
        title: isDE ? 'Marina-Eigentümer' : 'Marina Owners',
        desc: isDE
          ? 'Vollständige Kontrolle und Übersicht über alle Marinas, Finanzen und strategische Entscheidungen'
          : 'Complete control and overview of all marinas, finances, and strategic decisions',
      },
      operator: {
        title: isDE ? 'Betreiber' : 'Operators',
        desc: isDE
          ? 'Effiziente Verwaltung des Tagesgeschäfts, Mieter und Verträge'
          : 'Efficient management of daily operations, tenants, and contracts',
      },
      staff: {
        title: isDE ? 'Mitarbeiter' : 'Staff',
        desc: isDE
          ? 'Schneller Zugriff auf wichtige Informationen und Kommunikation mit Mietern'
          : 'Quick access to important information and communication with tenants',
      },
    },
    cta: {
      badge: isDE ? 'Bereit loszulegen?' : 'Ready to get started?',
      title1: isDE ? 'Modernisieren Sie Ihre' : 'Modernize Your',
      title2: isDE ? 'Marina-Operationen' : 'Marina Operations',
      subtitle: isDE
        ? 'Lernen Sie in einer Demo kennen, wie Marina Master Ihre Abläufe unterstützt.'
        : 'See in a demo how Marina Master supports your daily operations.',
      cta1: isDE ? 'Demo anfragen' : 'Request a Demo',
      cta2: isDE ? 'Gastbuchungs-Portal' : 'Guest Booking Portal',
    },
    footer: {
      desc: isDE
        ? 'Die moderne Marina-Management-Plattform. Optimieren Sie Abläufe, begeistern Sie Mieter und wachsen Sie mit KI-gestützten Tools.'
        : 'The modern marina management platform. Streamline operations, delight tenants, and grow with AI-powered tools.',
      product: isDE ? 'Produkt' : 'Product',
      features: isDE ? 'Funktionen' : 'Features',
      guestBooking: isDE ? 'Gastbuchung' : 'Guest Booking',
      signIn: isDE ? 'Anmelden' : 'Sign In',
      contact: isDE ? 'Kontakt' : 'Contact',
      privacy: isDE ? 'Datenschutz' : 'Privacy Policy',
      terms: isDE ? 'AGB' : 'Terms of Service',
      imprint: isDE ? 'Impressum' : 'Imprint',
    },
  };

  // Stats
  const stats = [
    { value: '8+', label: isDE ? 'Funktionskategorien' : 'Feature Categories' },
    { value: '35+', label: isDE ? 'Funktionen' : 'Features' },
    { value: '3', label: isDE ? 'Benutzerrollen' : 'User Roles' },
    { value: '2', label: isDE ? 'Sprachen' : 'Languages' },
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Marina Master</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                {isDE ? 'Funktionen' : 'Features'}
              </a>
              <a href="#roles" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                {isDE ? 'Rollen' : 'Roles'}
              </a>
              <Link href="/book" className="text-gray-600 hover:text-gray-900 font-medium transition-colors">
                {isDE ? 'Gastbuchung' : 'Guest Booking'}
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => handleLocaleChange('de')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-full transition-all',
                    locale === 'de'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  DE
                </button>
                <button
                  onClick={() => handleLocaleChange('en')}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-full transition-all',
                    locale === 'en'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  EN
                </button>
              </div>
              <Link href="/login">
                <Button variant="ghost" className="font-medium">
                  {isDE ? 'Anmelden' : 'Sign In'}
                </Button>
              </Link>
              <Button
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-medium shadow-lg hover:shadow-xl transition-all hidden sm:flex"
                onClick={() => window.location.href = 'mailto:contact@marina-master.de?subject=Demo Request'}
              >
                {t.hero.cta1}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 pt-32 pb-20 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl" />
        </div>

        {/* Wave SVG */}
        <svg className="absolute bottom-0 w-full h-[200px]" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.15)" />
              <stop offset="50%" stopColor="rgba(34, 211, 238, 0.12)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0.15)" />
            </linearGradient>
          </defs>
          <path
            fill="url(#waveGradient)"
            d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,165.3C672,171,768,213,864,218.7C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L0,320Z"
          />
        </svg>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/20">
                <Sparkles className="w-4 h-4 text-cyan-300" />
                <span className="text-white/90 text-sm font-medium">{t.hero.badge}</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                {t.hero.title1}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-200">
                  {t.hero.title2}
                </span>
              </h1>

              <p className="text-xl text-white/80 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {t.hero.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-white text-blue-900 hover:bg-gray-100 font-semibold text-lg px-8 py-6 rounded-xl shadow-xl hover:shadow-2xl transition-all"
                  onClick={() => window.location.href = 'mailto:contact@marina-master.de?subject=Demo Request'}
                >
                  {t.hero.cta1}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link href="/book">
                  <Button
                    size="lg"
                    className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl backdrop-blur-sm"
                  >
                    {t.hero.cta2}
                    <Ship className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right content - Stats card */}
            <div className="relative hidden lg:block">
              <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 shadow-2xl">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {isDE ? 'Umfassende Lösung' : 'Complete Solution'}
                  </h3>
                  <p className="text-white/60">
                    {isDE ? 'Alles in einer Plattform' : 'Everything in one platform'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, idx) => (
                    <div key={idx} className="text-center p-4 rounded-xl bg-white/5">
                      <div className="text-3xl font-bold text-cyan-300">{stat.value}</div>
                      <div className="text-sm text-white/60 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 text-emerald-400 justify-center">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {isDE ? 'KI-gestützte Funktionen' : 'AI-assisted features'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature pills */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Users className="w-5 h-5" />
              </div>
              <span className="font-medium">Multi-Marina</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Zap className="w-5 h-5" />
              </div>
              <span className="font-medium">{isDE ? 'Echtzeit-Sync' : 'Real-time Sync'}</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Globe className="w-5 h-5" />
              </div>
              <span className="font-medium">DE/EN</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <Shield className="w-5 h-5" />
              </div>
              <span className="font-medium">{isDE ? 'Rollen & Rechte' : 'Roles & permissions'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* USP Feature Cards Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-2 mb-4">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700 text-sm font-medium">
                {isDE ? 'Unsere Alleinstellungsmerkmale' : 'Our Unique Selling Points'}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {isDE ? 'Was uns' : 'What Makes Us'}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                {isDE ? 'besonders macht' : 'Special'}
              </span>
            </h2>
          </div>

          {/* Three main USP cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* AI Contract Processing */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-gradient-to-br from-violet-500 to-purple-600" />
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isDE ? 'KI-Vertragsverarbeitung' : 'AI Contract Processing'}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {isDE
                  ? 'Laden Sie PDF-Verträge hoch und lassen Sie Mieter, Preise und Laufzeiten per KI auslesen. Sie prüfen die Vorschläge, bevor sie gespeichert werden.'
                  : 'Upload PDF contracts and let AI read tenants, prices and terms. You review the suggestions before they are saved.'}
              </p>
              <div className="mt-4 flex items-center gap-2 text-violet-600">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">{isDE ? 'Powered by Claude AI' : 'Powered by Claude AI'}</span>
              </div>
            </div>

            {/* Full-Text Search */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-gradient-to-br from-blue-500 to-cyan-500" />
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isDE ? 'Blitzschnelle Volltextsuche' : 'Lightning-Fast Full-Text Search'}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {isDE
                  ? 'Finden Sie alles in Millisekunden: Mieter, Verträge, Boote, Rechnungen - eine Suche durchsucht alle Daten gleichzeitig.'
                  : 'Find anything in milliseconds: tenants, contracts, boats, invoices - one search queries all data simultaneously.'}
              </p>
              <div className="mt-4 flex items-center gap-2 text-blue-600">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">{isDE ? 'Sofortige Ergebnisse' : 'Instant Results'}</span>
              </div>
            </div>

            {/* WhatsApp Integration */}
            <div className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500 bg-gradient-to-br from-emerald-500 to-teal-500" />
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isDE ? 'WhatsApp Integration' : 'WhatsApp Integration'}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {isDE
                  ? 'Kommunizieren Sie direkt und persönlich mit Mietern und Gästen - von der Willkommensnachricht bis zur Buchungsbestätigung.'
                  : 'Communicate directly and personally with tenants and guests - from welcome messages to booking confirmations.'}
              </p>
              <div className="mt-4 flex items-center gap-2 text-emerald-600">
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{isDE ? 'Direkte Kommunikation' : 'Direct Communication'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-50 rounded-full px-4 py-2 mb-4">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-blue-700 text-sm font-medium">{t.features.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t.features.title1}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">
                {t.features.title2}
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              {t.features.subtitle}
            </p>

            {/* Expand/Collapse buttons */}
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" onClick={expandAll}>
                {t.features.expandAll}
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                {t.features.collapseAll}
              </Button>
            </div>
          </div>

          {/* Feature categories */}
          <div className="space-y-4">
            {featureCategories.map((category) => (
              <FeatureCategorySection
                key={category.id}
                category={category}
                isOpen={openCategories.has(category.id)}
                onToggle={() => toggleCategory(category.id)}
                locale={locale}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="py-24 bg-gradient-to-br from-slate-900 to-blue-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t.roles.title}
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              {isDE
                ? 'Ob Eigentümer, Betreiber, Mitarbeiter oder Mieter - Marina Master bietet für jede Rolle die passenden Funktionen.'
                : 'Whether owner, operator, staff, or tenant - Marina Master offers the right features for every role.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Owner */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4 shadow-lg">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t.roles.owner.title}</h3>
              <p className="text-white/70 text-sm">{t.roles.owner.desc}</p>
            </div>

            {/* Operator */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mb-4 shadow-lg">
                <UserCog className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t.roles.operator.title}</h3>
              <p className="text-white/70 text-sm">{t.roles.operator.desc}</p>
            </div>

            {/* Staff */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-4 shadow-lg">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{t.roles.staff.title}</h3>
              <p className="text-white/70 text-sm">{t.roles.staff.desc}</p>
            </div>

            {/* Tenant */}
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-4 shadow-lg">
                <Ship className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {isDE ? 'Mieter' : 'Tenants'}
              </h3>
              <p className="text-white/70 text-sm">
                {isDE
                  ? 'Self-Service-Portal für Rechnungen, Verträge, Dokumente und direkte Kommunikation mit der Marina.'
                  : 'Self-service portal for invoices, contracts, documents, and direct communication with the marina.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-white/20">
            <Anchor className="w-4 h-4 text-white" />
            <span className="text-white/90 text-sm font-medium">{t.cta.badge}</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t.cta.title1}
            <br />
            {t.cta.title2}
          </h2>

          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            {t.cta.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-blue-700 hover:bg-gray-100 font-semibold text-lg px-8 py-6 rounded-xl shadow-xl"
              onClick={() => window.location.href = 'mailto:contact@marina-master.de?subject=Demo Request'}
            >
              {t.cta.cta1}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Link href="/book">
              <Button
                size="lg"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl"
              >
                {t.cta.cta2}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
                  <Anchor className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">Marina Master</span>
              </div>
              <p className="text-gray-400 max-w-md">
                {t.footer.desc}
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.product}</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">{t.footer.features}</a></li>
                <li><Link href="/book" className="hover:text-white transition-colors">{t.footer.guestBooking}</Link></li>
                <li><Link href="/login" className="hover:text-white transition-colors">{t.footer.signIn}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t.footer.contact}</h4>
              <ul className="space-y-3 text-gray-400">
                <li><a href="mailto:contact@marina-master.de" className="hover:text-white transition-colors">contact@marina-master.de</a></li>
                <li><span>Wiesbaden, Germany</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Marina Master. All rights reserved.
            </p>
            <div className="flex gap-6 text-gray-500 text-sm">
              <Link href="/datenschutz" className="hover:text-white transition-colors">{t.footer.privacy}</Link>
              <Link href="/agb" className="hover:text-white transition-colors">{t.footer.terms}</Link>
              <Link href="/impressum" className="hover:text-white transition-colors">{t.footer.imprint}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
